import Job from '__core/shared/model/job/Job';
import JobItem from '__core/shared/model/job/JobItem';
import { JobProgressType } from '__core/shared/model/job/Job.enum';
import { preferredProduct as homeLoanPreferredProduct } from '__core/shared/helper/homeLoan';

import stJobAdd from './jobAdd.component';
import stJobEdit from './jobEdit.component';
import stJobProgressViewTile from './jobProgressViewTile.component';
import jobsController from '__core/client/controller/Jobs';
import stJobsFilter from './jobsFilter.component';
import stJobSimpleEdit from './jobSimpleEdit.component';
import stJobSimpleList from './jobSimpleList.component';
import stJobsList from './jobsList.component';
import stJobsListItem from './jobsListItem.component';
import jobsProgressViewController from './jobsProgressView.controller';
import jobsProgressViewService from './jobsProgressView.service';

export default angular
  .module('st.Job', ['st.Core', 'angular-cache'])
  .factory('Job', () => Job)
  .factory('JobItem', () => JobItem)
  .factory('JobProgress', () => JobProgressType)
  .component('stJobAdd', stJobAdd)
  .component('stJobEdit', stJobEdit)
  .component('stJobProgressViewTile', stJobProgressViewTile)
  .controller('jobsController', jobsController)
  .component('stJobsFilter', stJobsFilter)
  .component('stJobSimpleEdit', stJobSimpleEdit)
  .component('stJobSimpleList', stJobSimpleList)
  .component('stJobsList', stJobsList)
  .component('stJobsListItem', stJobsListItem)
  .controller('jobsProgressViewController', jobsProgressViewController)
  .service('jobsProgressViewService', jobsProgressViewService)
  .service('jobService', (Job, dataService) => ({
    createJob: (doc) => dataService.createDocument(Job, doc),
    readJob: (doc) => dataService.readDocument(Job, doc),
    updateJob: (doc) => dataService.updateDocument(Job, doc),
    deleteJob: (doc) => dataService.deleteDocument(Job, doc)
  }))
  .service('jobsService', ($http, $rootScope, CacheFactory, dataService, Job, JOB) => {
    let cache;
    if (!CacheFactory.get(JOB.CACHE.NAME)) {
      cache = CacheFactory(JOB.CACHE.NAME, JOB.CACHE.STORE);
    }
    const cacheKey = (params, requestName) =>
      JSON.stringify({
        idAccount: $rootScope.CurrentAccount.id,
        idOrganization: $rootScope.CurrentOrganization.id,
        type: requestName || JOB.CACHE.NAME,
        params
      });

    const calculateJobAbsoluteDueDate = ({ status, timeStamp, years }) => {
      if (status === 'paused') {
        return moment.utc(timeStamp, 'X').startOf('day').add(years, 'years').format('X');
      } else {
        return moment
          .utc(timeStamp, 'X')
          .startOf('day')
          .add(years, 'years')
          .subtract(2, 'months')
          .format('X');
      }
    };

    const jobBuild = (name, absoluteDueDate, ticket) => {
      const job = Job.build();
      job.isActive = true;
      job.idAssignedTo = ticket.idOwner;
      job.idStage = ticket.idStage;
      job.idTicket = ticket.id;
      job.idWorkflow = ticket.idWorkflow;
      job.name = name;
      job.isDueDateAbsolute = true;
      job.absoluteDueDate = absoluteDueDate;

      return job;
    };

    const mapJobsForHomeLoanProduct = (timeStamp, ticket) => {
      return (product) => {
        const productJobs = {
          name: product.productName,
          jobs: []
        };
        if (product.interestOnlyTerm) {
          const job = jobBuild(
            'IO expiry',
            calculateJobAbsoluteDueDate({ timeStamp, years: product.interestOnlyTerm }),
            ticket
          );
          productJobs.jobs.push(job);
        }

        if (product.fixedTerm) {
          const job = jobBuild(
            product?.selectedProduct?.residential?.rateType === 'FIXED'
              ? 'Fixed rate expiry'
              : 'Intro rate expiry',
            calculateJobAbsoluteDueDate({ timeStamp, years: product.fixedTerm }),
            ticket
          );
          productJobs.jobs.push(job);
        }

        return productJobs;
      };
    };

    const generatePostSettlementSwitchRefixJobList = (loan, ticket, timeStamp) => {
      return _.chain(loan?.switchProducts?.switchReviewProducts)
        .filter(({ isActive }) => isActive)
        .map((review) => review.productSplits)
        .flatten()
        .map(mapJobsForHomeLoanProduct(timeStamp, ticket))
        .value();
    };

    const generatePostSettlementHomeLoanProductSplitsJobList = (loan, ticket, timeStamp) =>
      _.map(
        homeLoanPreferredProduct(loan.reviewProducts)?.productSplits,
        mapJobsForHomeLoanProduct(timeStamp, ticket)
      );

    const generatePostSettlementJobListAssetFinance = (loan, ticket, timeStamp) => {
      const name = 'Asset finance end date';
      const term =
        loan?.financeProposal?.term ||
        ticket.assetFinance?.finance?.team ||
        ticket.assetFinance?.finance?.term ||
        0;

      return [
        {
          name: '',
          jobs: [
            jobBuild(name, calculateJobAbsoluteDueDate({ timeStamp, years: term / 12 }), ticket)
          ]
        }
      ];
    };

    const generatePostSettlementJobListBrokerTools = (loan, ticket, timeStamp) => {
      const switchRefixList = generatePostSettlementSwitchRefixJobList(loan, ticket, timeStamp);
      return switchRefixList.length
        ? switchRefixList
        : generatePostSettlementHomeLoanProductSplitsJobList(loan, ticket, timeStamp);
    };

    const generatePostSettlementJobList = (ticket, timeStamp) => {
      return ticket.loan.details.dealLoanDetailsSplit.map((split) => {
        const productJobs = {
          name: ticket.name,
          jobs: []
        };

        if (split.fixedYears) {
          const job = jobBuild(
            'Fixed Rate Expiry',
            calculateJobAbsoluteDueDate({ timeStamp, years: split.fixedYears }),
            ticket
          );

          productJobs.jobs.push(job);
        }
        if (split.ioYears) {
          const job = jobBuild(
            'IO Expiry',
            calculateJobAbsoluteDueDate({ timeStamp, years: split.ioYears }),
            ticket
          );
          productJobs.jobs.push(job);
        }

        return productJobs;
      });
    };

    const generateStatusChangeJobsFor = (status, { ticket, timeStamp }) => {
      const ticketJobs = {
        name: ticket.name,
        jobs: []
      };

      switch (status) {
        case 'paused':
          ticketJobs.jobs = [
            jobBuild(
              'Paused Deal Review',
              calculateJobAbsoluteDueDate({ status: 'paused', timeStamp, years: 0.6 }),
              ticket
            )
          ];
          return [ticketJobs];
        default:
          return [];
      }
    };

    return {
      clearCache: () => cache.removeAll(),
      getAvailable: (params) =>
        dataService.apiRequest(
          cache,
          cacheKey(params, 'getAvailable'),
          params?.ignoreCache === true,
          Job.build,
          $http.get,
          JOB.API.AVAILABLE,
          { params }
        ),
      getAvailablePerContact: (params) =>
        dataService.apiRequest(
          cache,
          cacheKey(params, 'getAvailablePerContact'),
          params?.ignoreCache === true,
          Job.build,
          $http.get,
          JOB.API.AVAILABLE_PER_CONTACT,
          { params }
        ),
      getAvailablePerTicket: (params) =>
        dataService.apiRequest(
          cache,
          cacheKey(params, 'getAvailablePerTicket'),
          params?.ignoreCache === true,
          Job.build,
          $http.get,
          JOB.API.AVAILABLE_PER_TICKET,
          { params }
        ),
      getTop: (params) =>
        dataService.apiRequest(
          cache,
          cacheKey(params, 'getTop'),
          params?.ignoreCache === true,
          Job.build,
          $http.get,
          JOB.API.TOP,
          { params }
        ),
      generatePostSettlementSwitchRefixJobList,
      generatePostSettlementHomeLoanProductSplitsJobList,
      generatePostSettlementJobListAssetFinance,
      generatePostSettlementJobListBrokerTools,
      generatePostSettlementJobList,
      generateStatusChangeJobsFor
    };
  })
  .constant('JOB', {
    API: {
      AVAILABLE: '/api/v1/jobs/available/',
      AVAILABLE_PER_CONTACT: '/api/v1/jobs/available-per-contact/',
      AVAILABLE_PER_TICKET: '/api/v1/jobs/available-per-ticket/',
      TOP: '/api/v1/jobs/top/'
    },
    FILTERS: {
      TEMPLATE: {
        path: 'jobTemplate.id',
        type: { name: 'str' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      },
      NAME: {
        path: 'name',
        type: { name: 'str' },
        operator: { name: 'inc' },
        operatorNot: false,
        values: []
      },
      ASSIGNED_TO: {
        path: 'idAssignedTo',
        type: { name: 'str' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      },
      WORKFLOW: {
        path: 'idWorkflow',
        type: { name: 'str' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      },
      TICKET: {
        path: 'idTicket',
        type: { name: 'str' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      },
      CONTACT: {
        path: 'idContact',
        type: { name: 'str' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      },
      DUE_DATE: {
        path: 'absoluteDueDate',
        type: { name: 'num' },
        operator: { name: 'btw' },
        operatorNot: false,
        values: []
      },
      PROGRESS: {
        path: 'jobProgress.name',
        type: { name: 'str' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      },
      STATUS_DUE_AHEAD: {
        path: 'isDueAhead',
        type: { name: 'bool' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      },
      STATUS_DUE: {
        path: 'isDue',
        type: { name: 'bool' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      },
      STATUS_OVERDUE: {
        path: 'isOverdue',
        type: { name: 'bool' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      },
      STATUS_COMPLETED: {
        path: 'isCompleted',
        type: { name: 'bool' },
        operator: { name: 'eq' },
        operatorNot: false,
        values: []
      }
    },
    UI: {
      PROGRESS_VIEW_HEADER_HEIGHT: 115,
      PROGRESS_VIEW_DIFFERENCE_HEIGHT: 40,
      SINGLE_JOB_HEIGHT: 61.2
    },
    CACHE: {
      NAME: 'jobsCache',
      STORE: {
        maxAge: 60 * 1000,
        deleteOnExpire: 'aggressive',
        recycleFreq: 30 * 1000,
        storageMode: 'sessionStorage'
      }
    }
  });
